import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChartPieSlice, Users, Receipt, SignOut, CalendarCheck, FileText, UserCircle, List, BookOpen } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import { toast } from "sonner";
import NotificationsBell from "@/components/NotificationsBell";
import { planCan } from "@/lib/plans";

function buildNav(user) {
  const base = [
    { to: "/", label: "Dashboard", icon: ChartPieSlice, end: true },
    { to: "/deudores", label: "Cartera", icon: Users },
    { to: "/agenda", label: "Agenda de Promesas", icon: CalendarCheck },
  ];
  if (planCan(user?.plan, "cotizaciones")) {
    base.push({ to: "/cotizaciones", label: "Cotización", icon: FileText });
  }
  if (planCan(user?.plan, "comprobantes")) {
    base.push({ to: "/comprobantes", label: "Comprobantes", icon: Receipt });
  }
  base.push(
    { to: "/educacion", label: "Educación Financiera", icon: BookOpen },
  );
  return base;
}

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [usage, setUsage] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchUsage = () => authApi.usage().then((u) => alive && setUsage(u)).catch(() => {});
    fetchUsage();
    const id = setInterval(fetchUsage, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [location.pathname]);

  // Cerrar drawer al navegar
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const nav = buildNav(user);
  const current = nav.find(
    (n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))
  );

  const handleLogout = async () => {
    await logout();
    toast.success("Sesión cerrada");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="w-64 border-r border-border bg-card hidden md:flex flex-col"
      >
        <div className="px-6 py-7 border-b border-border">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-bold text-2xl leading-none"
              style={{ fontFamily: '"Times New Roman", Times, serif' }}
            >
              R
            </div>
            <div>
              <div className="font-heading font-semibold tracking-tight text-lg leading-none">
                Recobro
              </div>
              <div className="overline mt-1.5">Cartera al día</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200",
                    isActive
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-foreground/80 hover:bg-accent hover:text-accent-foreground"
                  )
                }
              >
                <Icon size={20} weight="duotone" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-6 py-5 border-t border-border space-y-3">
          {user && (
            <div data-testid="user-info">
              <div className="overline">Sesión</div>
              <div className="text-sm font-medium truncate mt-1">{user.nombre_empresa}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              {usage && (
                <div className="mt-2 text-[11px] text-muted-foreground" data-testid="usage-counter">
                  <span className="font-mono font-medium text-foreground">{usage.deudores_actuales}</span>
                  <span className="mx-0.5">/</span>
                  <span className="font-mono">{usage.limite_deudores}</span> deudores · plan {usage.plan}
                </div>
              )}
            </div>
          )}
          <Link
            to="/perfil"
            data-testid="sidebar-perfil-link"
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
              location.pathname.startsWith("/perfil")
                ? "bg-primary/10 text-primary font-medium"
                : "text-foreground/80 hover:bg-accent"
            )}
          >
            <UserCircle size={16} weight="duotone" />
            Mi perfil
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground/80 hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <SignOut size={16} weight="bold" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-border bg-card sticky top-0 z-40">
          <div className="px-4 md:px-8 py-3 md:py-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                data-testid="mobile-menu-btn"
                aria-label="Abrir menú"
                className="md:hidden p-2 -ml-2 rounded-md text-foreground/80 hover:bg-accent transition-colors"
              >
                <List size={22} weight="bold" />
              </button>
              <div className="min-w-0">
                <div className="overline hidden sm:block">Módulo</div>
                <h1 className="font-heading font-semibold text-lg md:text-2xl tracking-tight mt-0.5 truncate">
                  {current?.label || "Recobro"}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <NotificationsBell />
            </div>
          </div>
        </header>

        {/* Mobile drawer */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-drawer">
            <SheetHeader className="px-5 py-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-bold text-2xl leading-none"
                  style={{ fontFamily: '"Times New Roman", Times, serif' }}
                >
                  R
                </div>
                <div className="text-left">
                  <SheetTitle className="font-heading font-semibold tracking-tight text-lg leading-none">
                    Recobro
                  </SheetTitle>
                  <div className="overline mt-1.5">Cartera al día</div>
                </div>
              </div>
            </SheetHeader>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {nav.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={`mnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all",
                        isActive
                          ? "bg-primary text-primary-foreground font-medium"
                          : "text-foreground/80 hover:bg-accent"
                      )
                    }
                  >
                    <Icon size={20} weight="duotone" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
            <div className="px-5 py-4 border-t border-border space-y-3">
              {user && (
                <div>
                  <div className="overline">Sesión</div>
                  <div className="text-sm font-medium truncate mt-1">{user.nombre_empresa}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  {usage && (
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      <span className="font-mono font-medium text-foreground">{usage.deudores_actuales}</span>
                      <span className="mx-0.5">/</span>
                      <span className="font-mono">{usage.limite_deudores}</span> · plan {usage.plan}
                    </div>
                  )}
                </div>
              )}
              <Link
                to="/perfil"
                data-testid="mobile-perfil-link"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground/80 hover:bg-accent transition-colors"
              >
                <UserCircle size={16} weight="duotone" />
                Mi perfil
              </Link>
              <button
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground/80 hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                <SignOut size={16} weight="bold" />
                Cerrar sesión
              </button>
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex-1 p-4 sm:p-6 md:p-8 animate-fade-in min-w-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
